import { markAsNotJunk, markAsJunk } from '../strings.locstring.json';
import {
    itemHeaderMarkAsUnread,
    itemHeaderMarkAsRead,
} from './getMailListItemPartContextMenuItems.locstring.json';
import { isFirstLevelExpanded } from 'owa-mail-list-store/lib/selectors/isConversationExpanded';
import getItemForMailList from 'owa-mail-store/lib/selectors/getItemForMailList';
import { itemHeaderForward } from 'owa-locstrings/lib/strings/itemheaderforward.locstring.json';
import { itemHeaderReplyAll } from 'owa-locstrings/lib/strings/itemheaderreplyall.locstring.json';
import { itemHeaderReply } from 'owa-locstrings/lib/strings/itemheaderreply.locstring.json';
import { itemMailListHeaderViewMessageDetails } from './itemheaderviewmessagedetails.locstring.json';
import { itemMailListHeaderViewMessageSource } from './itemheaderviewmessagesource.locstring.json';
import loc from 'owa-localize';
import { findEmailFromSender } from './getFilterableMailListContextMenuItems.locstring.json';
import { IContextualMenuItem, ContextualMenuItemType } from '@fluentui/react/lib/ContextualMenu';
import { listViewStore } from 'owa-mail-list-store';
import { onReply, onReplyAll, onForward } from 'owa-mail-commands';
import Message from 'owa-service/lib/contract/Message';
import doesFolderIdEqualName from 'owa-session-store/lib/utils/doesFolderIdEqualName';
import { lazyMarkItemJunkNotJunkFromReadingPane } from 'owa-mail-triage-action';
import { isJunkEmailEnabledByAdmin } from 'owa-mail-filterable-menu-behavior';
import { lazyMarkItemsAsReadBasedOnNodeIds } from 'owa-mail-mark-read-actions';
import folderStore, { isFolderUnderMsg } from 'owa-folders';
import { lazyFindEmailFromSender } from 'owa-mail-search';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { openMessageSourceDialog } from 'owa-mail-reading-pane-item-actions-view/lib/components/ViewMessageSourceDialog';
const contextMenuActionSource = 'ContextMenu';
import { ClientItem, mailStore } from 'owa-mail-store';
import { logUsage } from 'owa-analytics';

/**
 * Get an array of filterable menu items for mail list context menu
 * @param rowKeys the row keys to show the context menu
 * @param tableViewId the id of the table that contains these rows
 * @return the context menu items
 */
export default function getMailListItemPartContextMenuItems(
    nodeIds: string[],
    tableViewId: string,
    onDismiss: (ev?: any) => void
): IContextualMenuItem[] {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
    const expandedFirstLevel = isFirstLevelExpanded(selectedRowKeys[0]);
    const menuItems: IContextualMenuItem[] = [];
    if (nodeIds.length == 1) {
        const item: Message = getItemForMailList(nodeIds[0], expandedFirstLevel);
        const itemForViewMessage: ClientItem = mailStore.items.get(item.ItemId.Id);
        const isInJunkFolder = doesFolderIdEqualName(item.ParentFolderId.Id, 'junkemail');
        const currentFolder = folderStore.folderTable.get(tableView.tableQuery.folderId);
        const isFolderUnderMsgRoot = isFolderUnderMsg(currentFolder);
        if (isFolderUnderMsgRoot) {
            // Reply
            menuItems.push({
                key: 'replyItemPart',
                name: loc(itemHeaderReply),
                title: loc(itemHeaderReply),
                onClick: () => {
                    onReplyClicked();
                },
            });
        }

        // Reply All
        menuItems.push({
            key: 'replyAllItemPart',
            name: loc(itemHeaderReplyAll),
            title: loc(itemHeaderReplyAll),
            onClick: () => {
                onReplyAllClicked();
            },
        });

        // Forward
        menuItems.push({
            key: 'forwardItemPart',
            name: loc(itemHeaderForward),
            title: loc(itemHeaderForward),
            onClick: () => {
                onForwardClicked();
            },
        });
        menuItems.push({
            key: 'ItemPartDivider',
            itemType: ContextualMenuItemType.Divider,
        });

        const itemReadOrUnreadString = item.IsRead
            ? loc(itemHeaderMarkAsUnread)
            : loc(itemHeaderMarkAsRead);
        menuItems.push({
            key: 'markReadUnreadItemPart',
            name: itemReadOrUnreadString,
            title: itemReadOrUnreadString,
            onClick: () => {
                onMarkReadUnreadClicked(nodeIds, tableViewId, !item.IsRead);
            },
        });

        if (isFolderUnderMsgRoot && isJunkEmailEnabledByAdmin()) {
            const markAsJunkOrNotString = isInJunkFolder ? loc(markAsNotJunk) : loc(markAsJunk);
            menuItems.push({
                key: 'markJunkNotJunkItemPart',
                name: markAsJunkOrNotString,
                title: markAsJunkOrNotString,
                onClick: () => {
                    onMarkJunkNotJunkClicked(item.ItemId.Id, tableViewId, !isInJunkFolder);
                },
            });
        }

        const viewMessageorSourceString = isConsumer()
            ? loc(itemMailListHeaderViewMessageSource)
            : loc(itemMailListHeaderViewMessageDetails);
        menuItems.push({
            key: 'viewMessageDetails',
            name: viewMessageorSourceString,
            title: viewMessageorSourceString,
            onClick: () => {
                logUsage('TnS_ViewMessageDetails', [contextMenuActionSource, isConsumer()]);
                openMessageSourceDialog(itemForViewMessage);
            },
        });

        if (
            currentFolder &&
            !(currentFolder.DistinguishedFolderId === 'sentitems') &&
            !(currentFolder.DistinguishedFolderId === 'junkemail') &&
            !(currentFolder.DistinguishedFolderId === 'drafts')
        ) {
            const lastSender = item.Sender?.Mailbox;
            if (lastSender) {
                menuItems.push({
                    key: 'ItemPartDivider2',
                    itemType: ContextualMenuItemType.Divider,
                });
                menuItems.push({
                    key: 'findEmailFromSendersItemPart',
                    name: loc(findEmailFromSender),
                    title: loc(findEmailFromSender),
                    onClick: () => {
                        lazyFindEmailFromSender.importAndExecute(
                            lastSender.Name,
                            lastSender.EmailAddress
                        );
                    },
                });
            }
        }
    } else {
        // Only mark as read/unread supported for multiple item parts
        let hasUnreadItem = false;
        let hasReadItem = false;
        const itemIds: string[] = [];

        for (const nodeId of nodeIds) {
            const item: Message = getItemForMailList(nodeId, expandedFirstLevel);
            itemIds.push(item.ItemId.Id);

            if (item.IsRead) {
                hasReadItem = true;
            } else {
                hasUnreadItem = true;
            }
        }

        if (hasUnreadItem) {
            menuItems.push({
                key: 'markReadItemParts',
                name: loc(itemHeaderMarkAsRead),
                title: loc(itemHeaderMarkAsRead),
                onClick: () => {
                    onMarkReadUnreadClicked(nodeIds, tableViewId, true);
                },
            });
        }

        if (hasReadItem) {
            menuItems.push({
                key: 'markUnreadItemParts',
                name: loc(itemHeaderMarkAsUnread),
                title: loc(itemHeaderMarkAsUnread),
                onClick: () => {
                    onMarkReadUnreadClicked(nodeIds, tableViewId, false);
                },
            });
        }
    }

    return menuItems;
}

function onReplyClicked() {
    onReply(contextMenuActionSource);
}

function onReplyAllClicked() {
    onReplyAll(contextMenuActionSource);
}

function onForwardClicked() {
    onForward(contextMenuActionSource);
}

async function onMarkReadUnreadClicked(
    nodeIds: string[],
    tableViewId: string,
    shouldMarkAsRead: boolean
): Promise<void> {
    const markItemsAsReadBasedOnNodeIds = await lazyMarkItemsAsReadBasedOnNodeIds.import();
    markItemsAsReadBasedOnNodeIds(
        nodeIds,
        tableViewId,
        shouldMarkAsRead /* isReadValueToSet */,
        true /* isExplicit */,
        contextMenuActionSource
    );
}

function onMarkJunkNotJunkClicked(itemId: string, tableViewId: string, shouldMarkAsRead: boolean) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyMarkItemJunkNotJunkFromReadingPane.importAndExecute(
        itemId,
        tableView,
        contextMenuActionSource,
        null /* instrumentationContexts */
    );
}
